import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Tooltip,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, isAfter, isBefore, isEqual } from 'date-fns';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import axios from 'axios';



const PlanningVsActual = () => {
  // Helper function to format decimal hours as "Xh Ym"
  const formatHoursDisplay = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '-';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);

    if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };

  // State for filters - Default to 1 month range
  const defaultStartDate = new Date();
  defaultStartDate.setMonth(defaultStartDate.getMonth() - 1);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(new Date());
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedJobNumber, setSelectedJobNumber] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedJobCode, setSelectedJobCode] = useState('');
  const [selectedProjectStage, setSelectedProjectStage] = useState('');
  const [selectedDeliverable, setSelectedDeliverable] = useState('');

  // State for data
  const [projects, setProjects] = useState([]);
  const [jobNumbers, setJobNumbers] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [jobCodes, setJobCodes] = useState([]);
  const [projectStages, setProjectStages] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filteredJobCodes, setFilteredJobCodes] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);

  // State for UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // State for job number filtering
  const [jobNumberInput, setJobNumberInput] = useState('');
  const [filteredJobNumbers, setFilteredJobNumbers] = useState([]);


  // Fetch initial data (projects, users, job numbers, departments, job codes, project stages)
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [projectsRes, usersRes, projectUsersRes, departmentsRes, jobCodesRes, projectStagesRes] = await Promise.all([
          axios.get('/api/projects'),
          axios.get('/api/users'),
          axios.get('/api/project-users'),
          axios.get('/api/departments'),
          axios.get('/api/job-codes'),
          axios.get('/api/project-stages')
        ]);

        setProjects(projectsRes.data);
        setUsers(usersRes.data);
        setFilteredUsers(usersRes.data);
        setProjectUsers(projectUsersRes.data);
        setDepartments(departmentsRes.data);
        setJobCodes(jobCodesRes.data);
        setFilteredJobCodes(jobCodesRes.data);
        setProjectStages(projectStagesRes.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError('Failed to load initial data. Please try again.');
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);


  // Filter job numbers as user types
  useEffect(() => {
    if (jobNumberInput.trim() === '') {
      setFilteredJobNumbers(jobNumbers);
    } else {
      const filtered = jobNumbers.filter(jobNum =>
        jobNum.toString().toLowerCase().includes(jobNumberInput.toLowerCase())
      );
      setFilteredJobNumbers(filtered);
    }
  }, [jobNumberInput, jobNumbers]);

  // Update job numbers when project changes
  useEffect(() => {
    const fetchJobNumbers = async () => {
      if (selectedProject) {
        try {
          // Add a small delay to prevent rapid successive calls
          await new Promise(resolve => setTimeout(resolve, 100));
          const response = await axios.get(`/api/job-numbers?project_id=${selectedProject}`);
          setJobNumbers(response.data);

          // If there's only one job number, select it automatically
          if (response.data.length === 1) {
            setSelectedJobNumber(response.data[0]);
          } else {
            setSelectedJobNumber('');
          }
        } catch (err) {
          console.error('Error fetching job numbers:', err);
          // If rate limited, retry after a delay
          if (err.response?.status === 429) {
            console.log('Rate limited, retrying in 2 seconds...');
            setTimeout(() => fetchJobNumbers(), 2000);
          }
        }
      } else {
        // If no project is selected, fetch all job numbers
        try {
          // Add a small delay to prevent rapid successive calls
          await new Promise(resolve => setTimeout(resolve, 100));
          const response = await axios.get('/api/project-job-numbers');
          setJobNumbers(response.data);
          setSelectedJobNumber('');
        } catch (err) {
          console.error('Error fetching all job numbers:', err);
          // If rate limited, retry after a delay
          if (err.response?.status === 429) {
            console.log('Rate limited, retrying in 2 seconds...');
            setTimeout(() => fetchJobNumbers(), 2000);
          }
        }
      }
    };

    fetchJobNumbers();
  }, [selectedProject]);

  // Update filtered users when project changes
  useEffect(() => {
    if (selectedProject) {
      // Filter users assigned to the selected project
      const assignedUserIds = projectUsers
        .filter(pu => pu.project_id === parseInt(selectedProject))
        .map(pu => pu.user_id);

      const projectFilteredUsers = users.filter(user =>
        assignedUserIds.includes(user.id)
      );

      setFilteredUsers(projectFilteredUsers);

      // If the currently selected user is not in the filtered list, reset it
      if (selectedUser && !assignedUserIds.includes(parseInt(selectedUser))) {
        setSelectedUser('');
      }
    } else {
      // If no project is selected, show all users
      setFilteredUsers(users);
    }
  }, [selectedProject, projectUsers, users, selectedUser]);

  // Update filtered job codes when department changes
  useEffect(() => {
    if (selectedDepartment) {
      const departmentFilteredJobCodes = jobCodes.filter(jobCode =>
        jobCode.department_id === parseInt(selectedDepartment)
      );
      setFilteredJobCodes(departmentFilteredJobCodes);

      // If the currently selected job code is not in the filtered list, reset it
      if (selectedJobCode && !departmentFilteredJobCodes.find(jc => jc.id === parseInt(selectedJobCode))) {
        setSelectedJobCode('');
      }
    } else {
      // If no department is selected, show all job codes
      setFilteredJobCodes(jobCodes);
    }
  }, [selectedDepartment, jobCodes, selectedJobCode]);

  // Fetch comparison data when filters change
  const fetchComparisonData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Build query parameters - only include dates if they're set
      const params = {};

      if (startDate && endDate) {
        params.start_date = format(startDate, 'yyyy-MM-dd');
        params.end_date = format(endDate, 'yyyy-MM-dd');
      }

      if (selectedProject) params.project_id = selectedProject;
      if (selectedJobNumber) params.job_number = selectedJobNumber;
      if (selectedUser) params.user_id = selectedUser;
      if (selectedDepartment) params.department_id = selectedDepartment;
      if (selectedJobCode) params.job_code_id = selectedJobCode;
      if (selectedProjectStage) params.project_stage_id = selectedProjectStage;
      if (selectedDeliverable) params.deliverable = selectedDeliverable;

      console.log('🔍 Fetching reconciliation data with params:', params);

      const response = await axios.get('/api/reconciliation/planning-vs-actual', { params });
      setComparisonData(response.data);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching comparison data:', err);
      setError('Failed to load comparison data. Please try again.');
      setLoading(false);
    }
  }, [startDate, endDate, selectedProject, selectedJobNumber, selectedUser, selectedDepartment, selectedJobCode, selectedProjectStage, selectedDeliverable]);

  // Auto-fetch data on component mount and when initial data is loaded
  useEffect(() => {
    if (projects.length > 0 && users.length > 0) {
      console.log('🔄 Auto-fetching reconciliation data on component load');
      fetchComparisonData();
    }
  }, [projects.length, users.length, fetchComparisonData]);


  // Handle filter changes
  const handleProjectChange = (event) => {
    setSelectedProject(event.target.value);
  };

  const handleUserChange = (event) => {
    setSelectedUser(event.target.value);
  };

  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
  };

  const handleJobCodeChange = (event) => {
    setSelectedJobCode(event.target.value);
  };

  const handleProjectStageChange = (event) => {
    setSelectedProjectStage(event.target.value);
  };

  const handleDeliverableChange = (event) => {
    setSelectedDeliverable(event.target.value);
  };

  const handleStartDateChange = (date) => {
    // Ensure start date is not after end date
    if (date && (isBefore(date, endDate) || isEqual(date, endDate))) {
      setStartDate(date);
    }
  };

  const handleEndDateChange = (date) => {
    // Ensure end date is not before start date
    if (date && (isAfter(date, startDate) || isEqual(date, startDate))) {
      setEndDate(date);
    }
  };

  // Handle export to Excel
  const handleExportExcel = async () => {
    try {
      setLoading(true);

      // Build query parameters - only include dates if they're set
      const params = {
        format: 'excel'
      };

      if (startDate && endDate) {
        params.start_date = format(startDate, 'yyyy-MM-dd');
        params.end_date = format(endDate, 'yyyy-MM-dd');
      }

      if (selectedProject) params.project_id = selectedProject;
      if (selectedJobNumber) params.job_number = selectedJobNumber;
      if (selectedUser) params.user_id = selectedUser;
      if (selectedDepartment) params.department_id = selectedDepartment;
      if (selectedJobCode) params.job_code_id = selectedJobCode;
      if (selectedProjectStage) params.project_stage_id = selectedProjectStage;
      if (selectedDeliverable) params.deliverable = selectedDeliverable;

      console.log('📊 Exporting reconciliation data with params:', params);

      // Use axios to download the file with proper headers
      const response = await axios.get('/api/reconciliation/export', {
        params,
        responseType: 'blob', // Important for file downloads
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      // Create blob and download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with current date and applied filters
      const dateStr = new Date().toISOString().split('T')[0];
      const filterStr = selectedProject ? `-${selectedProject}` : '';
      link.download = `reconciliation-report-${dateStr}${filterStr}.xlsx`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setLoading(false);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setLoading(false);
      alert('Failed to export data. Please try again.');
    }
  };

  // Handle export to PDF
  const handleExportPDF = async () => {
    try {
      setLoading(true);

      // Build query parameters - only include dates if they're set
      const params = {
        format: 'pdf'
      };

      if (startDate && endDate) {
        params.start_date = format(startDate, 'yyyy-MM-dd');
        params.end_date = format(endDate, 'yyyy-MM-dd');
      }

      if (selectedProject) params.project_id = selectedProject;
      if (selectedJobNumber) params.job_number = selectedJobNumber;
      if (selectedUser) params.user_id = selectedUser;
      if (selectedDepartment) params.department_id = selectedDepartment;
      if (selectedJobCode) params.job_code_id = selectedJobCode;
      if (selectedProjectStage) params.project_stage_id = selectedProjectStage;
      if (selectedDeliverable) params.deliverable = selectedDeliverable;

      console.log('📄 Exporting reconciliation data to PDF with params:', params);

      // Use axios to download the file with proper headers
      const response = await axios.get('/api/reconciliation/export', {
        params,
        responseType: 'blob', // Important for file downloads
        headers: {
          'Accept': 'application/pdf'
        }
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with current date and applied filters
      const dateStr = new Date().toISOString().split('T')[0];
      const filterStr = selectedProject ? `-${selectedProject}` : '';
      link.download = `reconciliation-report-${dateStr}${filterStr}.pdf`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setLoading(false);
    } catch (err) {
      console.error('Error exporting to PDF:', err);
      setLoading(false);
      alert('Failed to export data. Please try again.');
    }
  };


  // Render variance cell with color coding
  const renderVarianceCell = (planned, actual) => {
    const variance = planned - actual;
    const percentVariance = planned !== 0 ? (variance / planned) * 100 : 0;
    let color = 'text.primary';

    // Only show critical (red) when actual hours exceed planned hours (negative variance)
    if (variance < 0 && Math.abs(percentVariance) > 20) {
      color = 'error.main'; // Critical - actual hours significantly exceed planned hours
    } else if (variance < 0 && Math.abs(percentVariance) > 10) {
      color = 'warning.main'; // Warning - actual hours moderately exceed planned hours
    } else if (Math.abs(percentVariance) <= 10) {
      color = 'text.primary'; // Within acceptable range
    } else {
      color = 'success.main'; // Under budget (positive variance)
    }

    return (
      <TableCell align="center" sx={{
        color: color,
        fontWeight: variance < 0 && Math.abs(percentVariance) > 10 ? 'bold' : 'normal'
      }}>
        {formatHoursDisplay(Math.abs(variance))}
      </TableCell>
    );
  };




  return (
    <Box>
      {/* Header Section */}
      <Box sx={{
        mb: 4,
        pb: 2,
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        backgroundImage: 'url(/geometric-pattern.svg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        borderRadius: 1,
        p: 3,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.85)',
          zIndex: 0
        }} />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#2D3A45', fontWeight: 600 }}>
            Work Planning vs. Timesheet Reconciliation
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#5A6A75' }} gutterBottom>
            Compare planned hours with actual logged hours to identify variances
          </Typography>
        </Box>
      </Box>

      {/* Filters Section */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Filter Options
        </Typography>

        {/* First Row - Date Range */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                format="dd/MM/yyyy"
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "medium"
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ flex: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                format="dd/MM/yyyy"
                onChange={handleEndDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "medium"
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        {/* Second Row - Project and Job Number */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl sx={{ flex: 1 }} fullWidth>
            <InputLabel id="project-select-label">Project</InputLabel>
            <Select
              labelId="project-select-label"
              value={selectedProject}
              onChange={handleProjectChange}
              label="Project"
            >
              <MenuItem value=""><em>All Projects</em></MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id.toString()}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1, position: 'relative' }}>
            <TextField
              fullWidth
              label="Job Number"
              value={jobNumberInput || selectedJobNumber}
              onChange={(e) => {
                const value = e.target.value;
                setJobNumberInput(value);
              }}
              placeholder="Type to search job numbers"
              size="medium"
            />
            {jobNumberInput && filteredJobNumbers.length > 0 && (
              <Paper sx={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 200, overflow: 'auto', zIndex: 1000, mt: 1 }}>
                {filteredJobNumbers.map((jobNum, index) => (
                  <Box
                    key={index}
                    onClick={() => {
                      // Find the project with this job number
                      const projectWithJobNumber = projects.find(p => p.job_number === jobNum);
                      if (projectWithJobNumber) {
                        setSelectedJobNumber(jobNum);
                        setSelectedProject(projectWithJobNumber.id.toString());
                      }
                      setJobNumberInput('');
                    }}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      borderBottom: '1px solid #eee'
                    }}
                  >
                    {jobNum}
                  </Box>
                ))}
              </Paper>
            )}
          </Box>
        </Box>

        {/* Third Row - Project Stage, Department, Deliverable */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl sx={{ flex: 1 }} fullWidth>
            <InputLabel id="project-stage-select-label">Project Stage</InputLabel>
            <Select
              labelId="project-stage-select-label"
              value={selectedProjectStage}
              onChange={handleProjectStageChange}
              label="Project Stage"
            >
              <MenuItem value=""><em>All Stages</em></MenuItem>
              {projectStages.map((stage) => (
                <MenuItem key={stage.id} value={stage.id.toString()}>
                  {stage.stage_number > 6 ? stage.name : `Stage ${stage.stage_number}: ${stage.name}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ flex: 1 }} fullWidth>
            <InputLabel id="department-select-label">Department</InputLabel>
            <Select
              labelId="department-select-label"
              value={selectedDepartment}
              onChange={handleDepartmentChange}
              label="Department"
            >
              <MenuItem value=""><em>All Departments</em></MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id.toString()}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            sx={{ flex: 1 }}
            fullWidth
            label="Deliverable"
            value={selectedDeliverable}
            onChange={handleDeliverableChange}
            placeholder="Enter deliverable"
            variant="outlined"
          />
        </Box>

        {/* Fourth Row - User */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl sx={{ flex: 1 }} fullWidth>
            <InputLabel id="user-select-label">User</InputLabel>
            <Select
              labelId="user-select-label"
              value={selectedUser}
              onChange={handleUserChange}
              label="User"
              disabled={loading || users.length === 0}
            >
              <MenuItem value=""><em>All Users</em></MenuItem>
              {filteredUsers.map((user) => (
                <MenuItem key={user.id} value={user.id.toString()}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Empty space to maintain layout consistency */}
          <Box sx={{ flex: 2 }}></Box>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        mb: 3,
        gap: 2,
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <Button
          variant="contained"
          onClick={fetchComparisonData}
          startIcon={<CompareArrowsIcon />}
          sx={{
            bgcolor: '#2D3A45',
            '&:hover': { bgcolor: '#3D4A55' },
            minWidth: 140
          }}
        >
          Compare Data
        </Button>

        <Button
          variant="outlined"
          onClick={handleExportExcel}
          startIcon={<FileDownloadIcon />}
          disabled={comparisonData.length === 0}
          sx={{
            borderColor: '#2D3A45',
            color: '#2D3A45',
            '&:hover': {
              borderColor: '#3D4A55',
              bgcolor: 'rgba(45, 58, 69, 0.04)'
            },
            minWidth: 140
          }}
        >
          Export to Excel
        </Button>

        <Button
          variant="outlined"
          onClick={handleExportPDF}
          startIcon={<PictureAsPdfIcon sx={{ color: '#FF5722' }} />}
          disabled={comparisonData.length === 0}
          sx={{
            borderColor: '#2D3A45',
            color: '#2D3A45',
            '&:hover': {
              borderColor: '#3D4A55',
              bgcolor: 'rgba(45, 58, 69, 0.04)'
            },
            minWidth: 140
          }}
        >
          Export to PDF
        </Button>
      </Box>

      {/* Active Filters Display */}
      {(selectedProject || selectedJobNumber || selectedUser || selectedDepartment || selectedJobCode || selectedProjectStage || selectedDeliverable) && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f9f9f9' }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#2D3A45', fontWeight: 500 }}>
            Active Filters:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {selectedProject && (
              <Chip
                label={`Project: ${projects.find(p => p.id === parseInt(selectedProject))?.name || 'Unknown'}`}
                color="primary"
                onDelete={() => setSelectedProject('')}
                size="medium"
                sx={{
                  bgcolor: '#2D3A45',
                  color: 'white',
                  '& .MuiChip-deleteIcon': { color: 'white' }
                }}
              />
            )}
            {selectedJobNumber && (
              <Chip
                label={`Job Number: ${selectedJobNumber}`}
                color="primary"
                onDelete={() => setSelectedJobNumber('')}
                size="medium"
                sx={{
                  bgcolor: '#2D3A45',
                  color: 'white',
                  '& .MuiChip-deleteIcon': { color: 'white' }
                }}
              />
            )}
            {selectedUser && (
              <Chip
                label={`User: ${users.find(u => u.id === parseInt(selectedUser))?.name || 'Unknown'}`}
                color="primary"
                onDelete={() => setSelectedUser('')}
                size="medium"
                sx={{
                  bgcolor: '#2D3A45',
                  color: 'white',
                  '& .MuiChip-deleteIcon': { color: 'white' }
                }}
              />
            )}
            {selectedDepartment && (
              <Chip
                label={`Dept: ${departments.find(d => d.id === parseInt(selectedDepartment))?.name || 'Unknown'}`}
                color="secondary"
                onDelete={() => setSelectedDepartment('')}
                size="medium"
              />
            )}
            {selectedJobCode && (
              <Chip
                label={`Code: ${filteredJobCodes.find(jc => jc.id === parseInt(selectedJobCode))?.code || 'Unknown'}`}
                color="info"
                onDelete={() => setSelectedJobCode('')}
                size="medium"
              />
            )}
            {selectedProjectStage && (
              <Chip
                label={`Stage: ${projectStages.find(ps => ps.id === parseInt(selectedProjectStage))?.name || 'Unknown'}`}
                color="warning"
                onDelete={() => setSelectedProjectStage('')}
                size="medium"
              />
            )}
            {selectedDeliverable && (
              <Chip
                label={`Deliverable: ${selectedDeliverable}`}
                color="info"
                onDelete={() => setSelectedDeliverable('')}
                size="medium"
              />
            )}
          </Box>
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: '16px', bgcolor: 'error.light' }}>
          <Typography color="error" variant="body1">
            {error}
          </Typography>
        </Paper>
      )}

      {/* Results Section */}
      {!loading && !error && comparisonData.length > 0 && (
        <>

          {/* Detailed Comparison Table */}
          <Paper sx={{
            p: 3,
            mb: 3,
            borderRadius: '16px',
            boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)',
            width: '100%',
            maxWidth: 'calc(100vw - 350px)', // Slightly more space
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2D3A45', fontWeight: 500 }}>
              Detailed Comparison
            </Typography>

            <TableContainer sx={{
              width: '100%',
              overflowX: 'auto',
              maxWidth: 'calc(100vw - 280px)', // Account for sidebar
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: 4,
              }
            }}>
              <Table size="small" sx={{
                minWidth: 1000,
                '& .MuiTableCell-root': {
                  padding: '4px 8px',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap'
                }
              }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#2D3A45' }}>
                    <TableCell rowSpan={2} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Job No.</TableCell>
                    <TableCell rowSpan={2} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Project</TableCell>
                    <TableCell rowSpan={2} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>User</TableCell>
                    <TableCell rowSpan={2} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Department</TableCell>
                    <TableCell rowSpan={2} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Project Stage</TableCell>
                    <TableCell rowSpan={2} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Deliverable</TableCell>
                    <TableCell rowSpan={2} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Comments</TableCell>
                    <TableCell rowSpan={2} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Rate</TableCell>
                    <TableCell colSpan={3} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Normal Hours</TableCell>
                    <TableCell colSpan={3} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Outside Normal Hours</TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#2D3A45' }}>
                    {/* Normal Hours */}
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Planned</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actual</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Variance</TableCell>

                    {/* Outside Normal Hours */}
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Planned</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actual</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Variance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell align="center">{row.job_number || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {row.project_name}
                        </Typography>
                        {row.item_no && (
                          <Typography variant="caption" color="text.secondary">
                            Item: {row.item_no}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">{row.user_name}</TableCell>
                      <TableCell align="center">
                        {row.department_name || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {row.project_stage_name || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {row.deliverable || '-'}
                      </TableCell>
                      <TableCell align="left" sx={{ maxWidth: '200px' }}>
                        {row.comments ? (
                          <Tooltip title={row.comments} arrow>
                            <Typography variant="body2" sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: 'help'
                            }}>
                              {row.comments}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        R{(row.hourly_rate || 0).toFixed(0)}
                      </TableCell>

                      {/* Normal Hours */}
                      <TableCell align="center">{formatHoursDisplay(row.planned_hours_normal)}</TableCell>
                      <TableCell align="center">{formatHoursDisplay(row.actual_hours_normal)}</TableCell>
                      {renderVarianceCell(row.planned_hours_normal || 0, row.actual_hours_normal || 0)}

                      {/* Outside Normal Hours */}
                      <TableCell align="center">{formatHoursDisplay(row.planned_hours_outside_normal)}</TableCell>
                      <TableCell align="center">{formatHoursDisplay(row.actual_hours_outside_normal)}</TableCell>
                      {renderVarianceCell(row.planned_hours_outside_normal || 0, row.actual_hours_outside_normal || 0)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>


        </>
      )}

      {/* No Data State */}
      {!loading && !error && comparisonData.length === 0 && (
        <Paper sx={{
          p: 4,
          textAlign: 'center',
          borderRadius: '16px',
          boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CompareArrowsIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No comparison data available
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Click "Compare Data" to view all reconciliation data, or apply filters to narrow down the results.
            </Typography>
            <Button
              variant="contained"
              onClick={fetchComparisonData}
              startIcon={<CompareArrowsIcon />}
              sx={{
                mt: 2,
                bgcolor: '#2D3A45',
                '&:hover': { bgcolor: '#3D4A55' }
              }}
            >
              Compare Data
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default PlanningVsActual;
